<script>
import { GlAvatar, GlLink } from '@gitlab/ui';
import TimeAgoTooltip from '~/vue_shared/components/time_ago_tooltip.vue';
import { sprintf } from '~/locale';
import { DECISION_SOURCE_TEXTS } from './constants';
import DecisionLogContext from './decision_log_context.vue';

export default {
  name: 'DecisionLogItem',
  components: {
    DecisionLogContext,
    GlAvatar,
    GlLink,
    TimeAgoTooltip,
  },
  props: {
    decision: {
      type: Object,
      required: true,
    },
  },
  emits: ['view-comment'],
  computed: {
    settledOptions() {
      return (this.decision.options?.nodes || []).filter((option) => option.selected);
    },
    // A record with no settled option should not exist, because a decision is only logged once an
    // option is chosen. Should one arrive, the question it was raised for still heads the card.
    heading() {
      return this.settledOptions[0]?.content || this.decision.title;
    },
    otherSettledOptions() {
      return this.settledOptions.slice(1);
    },
    // The question already heads the card when nothing was settled, so it is not repeated below.
    questionSubhead() {
      return this.settledOptions.length ? this.decision.title : null;
    },
    hasContext() {
      return Boolean(this.decision.description || this.decision.resolutionRationale);
    },
    sourceText() {
      const message = DECISION_SOURCE_TEXTS[this.decision.source];
      if (!message) return '';

      const author = this.decision.author?.name;
      // A source that names its author reads wrong without one, so the line is dropped instead.
      if (message.includes('%{author}')) {
        return author ? sprintf(message, { author }) : '';
      }

      return message;
    },
  },
};
</script>

<template>
  <li
    class="gl-mb-4 gl-list-none gl-rounded-lg gl-border-1 gl-border-solid gl-border-section gl-bg-subtle gl-p-4"
    data-testid="decision-log-item"
  >
    <!-- The decision leads the card, because that is what people scan a log for. The question it
      settled follows underneath, and is absent when the decision was marked from a thread. -->
    <h3 v-if="heading" class="gl-heading-4 gl-mb-1" data-testid="decision-answer">
      {{ heading }}
    </h3>

    <ul v-if="otherSettledOptions.length" class="gl-mb-1 gl-p-0">
      <li
        v-for="option in otherSettledOptions"
        :key="option.id"
        class="gl-heading-4 gl-mb-1 gl-list-none"
        data-testid="decision-answer"
      >
        {{ option.content }}
      </li>
    </ul>

    <p
      v-if="questionSubhead"
      class="gl-mb-3 gl-text-sm gl-text-subtle"
      data-testid="decision-question"
    >
      {{ questionSubhead }}
    </p>

    <div class="gl-flex gl-flex-wrap gl-items-center gl-gap-2 gl-text-sm gl-text-subtle">
      <gl-avatar
        :size="16"
        :src="decision.resolvedBy.avatarUrl"
        :entity-name="decision.resolvedBy.name"
      />
      <span class="gl-font-bold gl-text-default" data-testid="decision-decided-by">
        {{ decision.resolvedBy.name }}
      </span>
      <time-ago-tooltip :time="decision.resolvedAt" />
      <!-- The note lives on the page behind this panel, so the panel has to get out of the way
        for the link to land anywhere the reader can see. -->
      <gl-link
        v-if="decision.noteUrl"
        :href="decision.noteUrl"
        class="gl-text-sm"
        data-testid="decision-comment-link"
        @click="$emit('view-comment')"
      >
        {{ s__('WorkItemDecisionLog|View comment') }}
      </gl-link>
    </div>

    <p
      v-if="sourceText"
      class="gl-mb-0 gl-mt-2 gl-text-sm gl-text-subtle"
      data-testid="decision-source"
    >
      {{ sourceText }}
    </p>

    <decision-log-context
      v-if="hasContext"
      :context="decision.description"
      :rationale="decision.resolutionRationale"
    />
  </li>
</template>
